import { t } from 'i18next';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { TBoundAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import { apiServiceAsync, retryableApiServiceAsync } from '../services/apiService';
import history from '../../../../../shared/frontend/libs/history';
import { delayForMs } from '../../../../../shared/common/utils';
import { LOCATION_CHANGE } from '../../../../../shared/frontend/libs/router/redux-store';
import { trackRecapGenerateComplete, trackRecapGenerateFail } from './tww/lib/recapAnalytics';
import { getReviewPollDelayMs } from './reviewPollDelay';

//
// Actions
//

type TInReviewClientState = {};

// delete review

type TDeleteReviewRequestedParams = {
  reviewSlug: string;
};

type TDeleteReviewSucceededParams = TDeleteReviewRequestedParams & {
  results: any;
};

type TDeleteReviewFailedParams = TDeleteReviewRequestedParams & {
  error: any;
};

// create review

type TCreateReviewRequestedParams = {
  reviewSlug: string;
};

type TCreateReviewSucceededParams = TCreateReviewRequestedParams & {
  results: any;
};

type TCreateReviewAbortedParams = {};

type TCreateReviewFailedParams = TCreateReviewRequestedParams & {
  error: any;
};

// open review

type TOpenReviewRequestedParams = {
  id: string;
  reviewSlug: string;
};

type TOpenReviewSucceededParams = TOpenReviewRequestedParams & {
  results: any;
};

type TOpenReviewFailedParams = TOpenReviewRequestedParams & {
  error: any;
};

// set review tokens

type TSetReviewTokensRequestedParams = {
  id: string;
  reviewSlug: string;
  type: string;
};

type TSetReviewTokensSucceededParams = TSetReviewTokensRequestedParams & {
  results: any;
};

type TSetReviewTokensFailedParams = TSetReviewTokensRequestedParams & {
  error: any;
};
export const enum ActionTypes {
  SET_DELETE_REVIEW_REQUESTED = 'IN_REVIEW/DELETE_REVIEW_REQUESTED',
  SET_DELETE_REVIEW_SUCCEEDED = 'IN_REVIEW/DELETE_REVIEW_SUCCEEDED',
  SET_DELETE_REVIEW_FAILED = 'IN_REVIEW/DELETE_REVIEW_FAILED',

  SET_CREATE_REVIEW_REQUESTED = 'IN_REVIEW/CREATE_REVIEW_REQUESTED',
  SET_CREATE_REVIEW_SUCCEEDED = 'IN_REVIEW/CREATE_REVIEW_SUCCEEDED',
  SET_CREATE_REVIEW_ABORTED = 'IN_REVIEW/CREATE_REVIEW_ABORTED',
  SET_CREATE_REVIEW_FAILED = 'IN_REVIEW/CREATE_REVIEW_FAILED',

  SET_OPEN_REVIEW_REQUESTED = 'IN_REVIEW/OPEN_REVIEW_REQUESTED',
  SET_OPEN_REVIEW_SUCCEEDED = 'IN_REVIEW/OPEN_REVIEW_SUCCEEDED',
  SET_OPEN_REVIEW_FAILED = 'IN_REVIEW/OPEN_REVIEW_FAILED',

  SET_REVIEW_TOKENS_REQUESTED = 'IN_REVIEW/REVIEW_TOKENS_REQUESTED',
  SET_REVIEW_TOKENS_SUCCEEDED = 'IN_REVIEW/REVIEW_TOKENS_SUCCEEDED',
  SET_REVIEW_TOKENS_FAILED = 'IN_REVIEW/REVIEW_TOKENS_FAILED'
}

// delete review
type TActionDeleteReviewRequested = TBoundAction<ActionTypes.SET_DELETE_REVIEW_REQUESTED, TDeleteReviewRequestedParams>;
type TActionDeleteReviewSucceeded = TBoundAction<ActionTypes.SET_DELETE_REVIEW_SUCCEEDED, TDeleteReviewSucceededParams>;
type TActionDeleteReviewFailed = TBoundAction<ActionTypes.SET_DELETE_REVIEW_FAILED, TDeleteReviewFailedParams>;

// create review
type TActionCreateReviewRequested = TBoundAction<ActionTypes.SET_CREATE_REVIEW_REQUESTED, TCreateReviewRequestedParams>;
type TActionCreateReviewSucceeded = TBoundAction<ActionTypes.SET_CREATE_REVIEW_SUCCEEDED, TCreateReviewSucceededParams>;
type TActionCreateReviewAborted = TBoundAction<ActionTypes.SET_CREATE_REVIEW_ABORTED, TCreateReviewAbortedParams>;
type TActionCreateReviewFailed = TBoundAction<ActionTypes.SET_CREATE_REVIEW_FAILED, TCreateReviewFailedParams>;

// open review
type TActionOpenReviewRequested = TBoundAction<ActionTypes.SET_OPEN_REVIEW_REQUESTED, TOpenReviewRequestedParams>;
type TActionOpenReviewSucceeded = TBoundAction<ActionTypes.SET_OPEN_REVIEW_SUCCEEDED, TOpenReviewSucceededParams>;
type TActionOpenReviewFailed = TBoundAction<ActionTypes.SET_OPEN_REVIEW_FAILED, TOpenReviewFailedParams>;

// set review tokens
type TActionSetReviewTokensRequested = TBoundAction<
  ActionTypes.SET_REVIEW_TOKENS_REQUESTED,
  TSetReviewTokensRequestedParams
>;
type TActionSetReviewTokensSucceeded = TBoundAction<
  ActionTypes.SET_REVIEW_TOKENS_SUCCEEDED,
  TSetReviewTokensSucceededParams
>;
type TActionSetReviewTokensFailed = TBoundAction<ActionTypes.SET_REVIEW_TOKENS_FAILED, TSetReviewTokensFailedParams>;

// location
type TActionLocationChanged = TBoundAction<typeof LOCATION_CHANGE, any>;

// supported
type TSupportedActions =
  | TActionDeleteReviewRequested
  | TActionDeleteReviewSucceeded
  | TActionDeleteReviewFailed
  | TActionCreateReviewRequested
  | TActionCreateReviewSucceeded
  | TActionCreateReviewAborted
  | TActionCreateReviewFailed
  | TActionOpenReviewRequested
  | TActionOpenReviewSucceeded
  | TActionOpenReviewFailed
  | TActionSetReviewTokensRequested
  | TActionSetReviewTokensSucceeded
  | TActionSetReviewTokensFailed;

export type TActions = {
  deleteReviewRequested(params: TDeleteReviewRequestedParams): TActionDeleteReviewRequested;
  deleteReviewSucceeded(params: TDeleteReviewSucceededParams): TActionDeleteReviewSucceeded;
  deleteReviewFailed(params: TDeleteReviewFailedParams): TActionDeleteReviewFailed;

  createReviewRequested(params: TCreateReviewRequestedParams): TActionCreateReviewRequested;
  createReviewSucceeded(params: TCreateReviewSucceededParams): TActionCreateReviewSucceeded;
  createReviewAborted(): any;
  createReviewFailed(params: TCreateReviewFailedParams): TActionCreateReviewFailed;

  openReviewRequested(params: TOpenReviewRequestedParams): TActionOpenReviewRequested;
  openReviewSucceeded(params: TOpenReviewSucceededParams): TActionOpenReviewSucceeded;
  openReviewFailed(params: TOpenReviewFailedParams): TActionOpenReviewFailed;

  setReviewTokensRequested(params: TSetReviewTokensRequestedParams): TActionSetReviewTokensRequested;
  setReviewTokensSucceeded(params: TSetReviewTokensSucceededParams): TActionSetReviewTokensSucceeded;
  setReviewTokensFailed(params: TSetReviewTokensFailedParams): TActionSetReviewTokensFailed;
};

export const actions: TActions = {
  //
  // Delete Review
  //
  deleteReviewRequested(params: TDeleteReviewRequestedParams) {
    return { type: ActionTypes.SET_DELETE_REVIEW_REQUESTED, payload: params };
  },

  deleteReviewSucceeded(params: TDeleteReviewSucceededParams) {
    return { type: ActionTypes.SET_DELETE_REVIEW_SUCCEEDED, payload: params };
  },

  deleteReviewFailed(params: TDeleteReviewFailedParams) {
    return { type: ActionTypes.SET_DELETE_REVIEW_FAILED, payload: params };
  },

  //
  // Create Review
  //
  createReviewRequested(params: TCreateReviewRequestedParams) {
    return { type: ActionTypes.SET_CREATE_REVIEW_REQUESTED, payload: params };
  },

  createReviewSucceeded(params: TCreateReviewSucceededParams) {
    return { type: ActionTypes.SET_CREATE_REVIEW_SUCCEEDED, payload: params };
  },

  createReviewAborted() {
    return { type: ActionTypes.SET_CREATE_REVIEW_ABORTED };
  },

  createReviewFailed(params: TCreateReviewFailedParams) {
    return { type: ActionTypes.SET_CREATE_REVIEW_FAILED, payload: params };
  },

  //
  // Open Review
  //
  openReviewRequested(params: TOpenReviewRequestedParams) {
    return { type: ActionTypes.SET_OPEN_REVIEW_REQUESTED, payload: params };
  },

  openReviewSucceeded(params: TOpenReviewSucceededParams) {
    return {
      type: ActionTypes.SET_OPEN_REVIEW_SUCCEEDED,
      payload: params
    };
  },

  openReviewFailed(params: TOpenReviewFailedParams) {
    return {
      type: ActionTypes.SET_OPEN_REVIEW_FAILED,
      payload: params
    };
  },

  //
  // Set Review Tokens
  //
  setReviewTokensRequested(params: TSetReviewTokensRequestedParams) {
    return { type: ActionTypes.SET_REVIEW_TOKENS_REQUESTED, payload: params };
  },

  setReviewTokensSucceeded(params: TSetReviewTokensSucceededParams) {
    return {
      type: ActionTypes.SET_REVIEW_TOKENS_SUCCEEDED,
      payload: params
    };
  },

  setReviewTokensFailed(params: TSetReviewTokensFailedParams) {
    return {
      type: ActionTypes.SET_REVIEW_TOKENS_FAILED,
      payload: params
    };
  }
};

//
// Reducer
//

function emptySlice() {
  return {
    request: {} as any,
    response: {} as any,
    isRequested: false,
    isSucceeded: false,
    isErrored: false
  };
}

function getInitialState() {
  return {
    createReview: emptySlice(),
    deleteReview: emptySlice(),
    openReview: emptySlice(),
    reviewTokens: emptySlice()
  };
}

export function reducer(state = getInitialState(), action: TSupportedActions): Partial<TInReviewClientState> {
  switch (action.type) {
    // delete review
    case ActionTypes.SET_DELETE_REVIEW_REQUESTED: {
      return {
        ...state,
        deleteReview: {
          ...state.deleteReview,
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_DELETE_REVIEW_SUCCEEDED: {
      return {
        ...state,
        deleteReview: {
          ...state.deleteReview,
          response: action.payload.results,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_DELETE_REVIEW_FAILED: {
      return {
        ...state,
        deleteReview: {
          ...state.deleteReview,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    // create review
    case ActionTypes.SET_CREATE_REVIEW_REQUESTED: {
      return {
        ...state,
        createReview: {
          ...state.createReview,
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false,
          isAborted: false
        }
      };
    }

    // create review
    case ActionTypes.SET_CREATE_REVIEW_ABORTED: {
      return {
        ...state,
        createReview: {
          ...state.createReview,
          isRequested: false,
          isSucceeded: false,
          isErrored: false,
          isAborted: true
        },
        reviewTokens: {
          ...getInitialState().reviewTokens
        }
      };
    }

    case ActionTypes.SET_CREATE_REVIEW_SUCCEEDED: {
      return {
        ...state,
        createReview: {
          ...state.createReview,
          response: action.payload.results,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          isAborted: false
        }
      };
    }

    case ActionTypes.SET_CREATE_REVIEW_FAILED: {
      return {
        ...state,
        createReview: {
          ...state.createReview,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload,
          isAborted: false
        }
      };
    }

    // open review
    case ActionTypes.SET_OPEN_REVIEW_REQUESTED: {
      return {
        ...state,
        openReview: {
          ...state.openReview,
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_OPEN_REVIEW_SUCCEEDED: {
      return {
        ...state,
        openReview: {
          ...state.openReview,
          request: {
            id: action.payload.id
          },
          response: action.payload.results,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_OPEN_REVIEW_FAILED: {
      return {
        ...state,
        openReview: {
          ...state.openReview,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    // set review tokens
    case ActionTypes.SET_REVIEW_TOKENS_REQUESTED: {
      return {
        ...state,
        reviewTokens: {
          ...state.reviewTokens,
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_REVIEW_TOKENS_SUCCEEDED: {
      return {
        ...state,
        reviewTokens: {
          ...state.reviewTokens,
          request: {
            id: action.payload.id
          },
          response: {
            ...state.reviewTokens.response,
            [action.payload.reviewSlug + action.payload.id]: {
              ...(state.reviewTokens.response as any)[action.payload.reviewSlug + action.payload.id],
              ...action.payload.results
            }
          },
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_REVIEW_TOKENS_FAILED: {
      return {
        ...state,
        reviewTokens: {
          ...state.reviewTokens,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }
  }

  return state;
}

const getIsAborted = (state: any) => state.inReview.createReview.isAborted;

// When the session has expired, auth-required API calls get redirected to the
// login page and the HTML body is swallowed as an empty {} "success" — an empty
// response on these endpoints means "not logged in", not a server failure.
function looksLoggedOut(results: any): boolean {
  return !results || (typeof results === 'object' && Object.keys(results).length === 0);
}

//
// Sagas
//

//
// create review requested
//
export function* createReviewRequested(action: TActionCreateReviewRequested): any {
  try {
    const results = yield call(apiServiceAsync.createReview, action.payload.reviewSlug);
    if (looksLoggedOut(results)) {
      throw new Error(t('recaps:creator.shared.error-not-logged-in'));
    }
    let automaticRetryUsed = false;

    while (true) {
      const isAborted = yield select(getIsAborted);
      if (isAborted) {
        break;
      }

      const jobResults = yield call(apiServiceAsync.getReviewJobStatus, action.payload.reviewSlug);
      if (looksLoggedOut(jobResults)) {
        throw new Error(t('recaps:creator.shared.error-not-logged-in'));
      }
      if (!jobResults.status) {
        throw new Error('Failed to create review');
      }

      if (jobResults.status === 'retry') {
        if (automaticRetryUsed) {
          throw new Error('Failed to create review');
        }
        automaticRetryUsed = true;
        const retryResults = yield call(apiServiceAsync.createReview, action.payload.reviewSlug);
        if (looksLoggedOut(retryResults)) {
          throw new Error(t('recaps:creator.shared.error-not-logged-in'));
        }
        continue;
      }

      if (jobResults.status === 'ready') {
        yield put(
          actions.createReviewSucceeded({
            results: jobResults.createReviewApi,
            reviewSlug: action.payload.reviewSlug
          })
        );
        trackRecapGenerateComplete(action.payload.reviewSlug);

        if (jobResults.openReviewApi) {
          yield put(
            actions.openReviewSucceeded({
              id: jobResults.openReviewApi.id,
              reviewSlug: action.payload.reviewSlug,
              results: jobResults.openReviewApi
            })
          );
        }

        if (jobResults.createReviewApi) {
          history.push(jobResults.createReviewApi.path);
        }
        break;
      }

      yield delayForMs(getReviewPollDelayMs());
    }
  } catch (e) {
    trackRecapGenerateFail(action.payload.reviewSlug, e.toString());
    yield put(
      actions.createReviewFailed({
        reviewSlug: action.payload.reviewSlug,
        error: e.message || String(e)
      })
    );
  }
}

function* watchCreateReviewRequests(): any {
  yield takeLatest(ActionTypes.SET_CREATE_REVIEW_REQUESTED, createReviewRequested);
}

function* triggerLocationChanged(action: TActionLocationChanged): any {
  if (!action.payload.isFirstRendering) {
    yield put(actions.createReviewAborted());
  }
}

function* watchLocationChanged(): any {
  yield takeLatest(LOCATION_CHANGE, triggerLocationChanged);
}

//
// delete an already created review for this user
//
function* deleteReviewRequested(action: TActionDeleteReviewRequested): any {
  try {
    const results = yield call(apiServiceAsync.deleteReview, action.payload.reviewSlug);
    if (looksLoggedOut(results)) {
      throw new Error(t('recaps:creator.shared.error-not-logged-in'));
    }
    yield put(
      actions.deleteReviewSucceeded({
        reviewSlug: action.payload.reviewSlug,
        results: results.deleteReviewApi
      })
    );
  } catch (e) {
    yield put(
      actions.deleteReviewFailed({
        reviewSlug: action.payload.reviewSlug,
        error: e.toString()
      })
    );
  }
}

function* watchDeleteReviewRequested(): any {
  yield takeLatest(ActionTypes.SET_DELETE_REVIEW_REQUESTED, deleteReviewRequested);
}

//
// view an already generated review
//
function* openReviewRequested(action: TActionOpenReviewRequested): any {
  const { id, reviewSlug } = action.payload;
  try {
    const results = yield call(apiServiceAsync.openReview, { id, reviewSlug });
    yield put(
      actions.openReviewSucceeded({
        reviewSlug: action.payload.reviewSlug,
        id,
        results: results.openReviewApi
      })
    );
  } catch (e) {
    yield put(actions.openReviewFailed({ id, reviewSlug, error: e.toString() }));
  }
}

function* watchOpenReviewRequested(): any {
  yield takeLatest(ActionTypes.SET_OPEN_REVIEW_REQUESTED, openReviewRequested);
}

//
// view tokens for a review
//
function* setReviewTokensRequested(action: TActionSetReviewTokensRequested): any {
  const { id, reviewSlug, type } = action.payload;
  try {
    const results = yield call(retryableApiServiceAsync.getReviewStatsTokens, { id, reviewSlug, type });
    yield put(
      actions.setReviewTokensSucceeded({
        type,
        id,
        reviewSlug: action.payload.reviewSlug,
        results: results.tokens
      })
    );
  } catch (e) {
    yield put(actions.setReviewTokensFailed({ id, reviewSlug, type, error: e.toString() }));
  }
}

function* watchSetReviewTokensRequested(): any {
  yield takeEvery(ActionTypes.SET_REVIEW_TOKENS_REQUESTED, setReviewTokensRequested);
}

//
// saga exports
//
export const sagas = [
  watchCreateReviewRequests(),
  watchDeleteReviewRequested(),
  watchOpenReviewRequested(),
  watchLocationChanged(),
  watchSetReviewTokensRequested()
];
